import { useEffect, useState, useCallback } from 'react';

function isRunningStandalone() {
  if (window.matchMedia?.('(display-mode: standalone)').matches) return true;
  if (window.matchMedia?.('(display-mode: window-controls-overlay)').matches) return true;
  // iOS Safari (PWA sudah ditambahkan ke Home Screen)
  if (window.navigator.standalone === true) return true;
  return false;
}

// Deteksi kasar buat fallback manual — bukan buat gating fitur secara umum,
// cuma dipakai supaya tombol "Install" tetap muncul di Android lawas
// (mis. Android 5.0/Lollipop ke bawah, browser bawaan non-Chromium, dll.)
// yang tidak pernah menembakkan event `beforeinstallprompt`.
function isAndroid() {
  return /Android/i.test(navigator.userAgent || '');
}

function isIOS() {
  return /iPhone|iPad|iPod/i.test(navigator.userAgent || '');
}

/**
 * Mengelola tombol "Install Aplikasi".
 *
 * Event `beforeinstallprompt` ditangkap oleh inline script paling awal di
 * index.html (bukan di sini) — supaya tidak ada race condition kalau event
 * itu nembak sebelum React sempat mount dan pasang listener-nya sendiri.
 * Hook ini cuma baca hasil tangkapannya lewat `window.__pwaInstallPrompt`
 * dan custom event `pwa-install-available` / `pwa-install-done`.
 *
 * - installable: true kalau ada prompt native yang sudah ditangkap dan
 *   aplikasi belum terpasang -> tombol boleh muncul, promptInstall() akan
 *   memicu dialog konfirmasi native OS.
 * - manualInstallable: true kalau browser/OS tidak pernah mendukung
 *   `beforeinstallprompt` (mis. Android 5.0 ke bawah, browser non-Chromium,
 *   iOS Safari) tapi platformnya mobile -> tombol tetap boleh muncul, tapi
 *   promptInstall() harus diganti dengan panduan manual (lihat
 *   InstallAppModal, mode="manual").
 * - isInstalled: true kalau aplikasi sudah berjalan sebagai PWA -> tombol disembunyikan.
 * - promptInstall(): memicu dialog instalasi native. Return false kalau
 *   tidak ada prompt native yang tersedia (harus pakai fallback manual).
 */
export function usePwaInstall() {
  const [hasPrompt, setHasPrompt] = useState(() => !!window.__pwaInstallPrompt);
  const [isInstalled, setIsInstalled] = useState(() => isRunningStandalone());

  useEffect(() => {
    // Jaga-jaga: event sudah keburu nembak sebelum komponen ini mount.
    setHasPrompt(!!window.__pwaInstallPrompt);

    const onAvailable = () => setHasPrompt(!!window.__pwaInstallPrompt);
    const onDone = () => { setHasPrompt(false); setIsInstalled(true); };
    const displayModeMedia = window.matchMedia?.('(display-mode: standalone)');
    const onDisplayModeChange = (e) => setIsInstalled(e.matches || isRunningStandalone());

    window.addEventListener('pwa-install-available', onAvailable);
    window.addEventListener('pwa-install-done', onDone);
    window.addEventListener('appinstalled', onDone);
    displayModeMedia?.addEventListener?.('change', onDisplayModeChange);

    return () => {
      window.removeEventListener('pwa-install-available', onAvailable);
      window.removeEventListener('pwa-install-done', onDone);
      window.removeEventListener('appinstalled', onDone);
      displayModeMedia?.removeEventListener?.('change', onDisplayModeChange);
    };
  }, []);

  const promptInstall = useCallback(async () => {
    const promptEvent = window.__pwaInstallPrompt;
    if (!promptEvent) return false;
    promptEvent.prompt();
    const { outcome } = await promptEvent.userChoice;
    window.__pwaInstallPrompt = null;
    setHasPrompt(false);
    return outcome === 'accepted';
  }, []);

  // Tombol instal manual (tanpa API beforeinstallprompt) cuma ditawarkan di
  // Android lama & iOS Safari yang memang tidak punya jalur native — bukan
  // di desktop, supaya tidak muncul di browser yang memang tidak
  // menyediakan instalasi PWA sama sekali.
  const manualInstallable = !hasPrompt && !isInstalled && (isAndroid() || isIOS());

  return {
    installable: hasPrompt && !isInstalled,
    manualInstallable,
    isInstalled,
    promptInstall,
  };
}